import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { renderAmount } from "../../../../utils";
import _ from "lodash";
import {
  FolderPlusIcon,
  PencilSquareIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { EditableText } from "../../../../shared";
import { accessoryOptions } from "../../../../constants/select";
import { useForm } from "../../../../hooks/useForm";
import { useMemo } from "react";
import { renderCustomColumnDefs } from "../../../../shared/Table/table.utils";
import { usePermissions } from "../../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../../constants/access-control";

// const columnHelper = createColumnHelper();

const AccessoryTable = ({ data, editAcc, removeAcc, jobLower = "job", ...props }) => {
  const formHook = useForm();
  const { hasPermission } = usePermissions();
  const canViewMoney = hasPermission(PERMISSIONS.JOBS_EDIT);

  const tableCols = [
    {
      id: "type",
      header: "Type",
      accessor: "type",
      accessorFn: (originalRow) => originalRow.type,
      minSize: 300,
      cell: ({ getValue, row }) => {
        const isEditing = row.original.editing;
        return (
          <TableItem
            id="type"
            type="select"
            placeholder="Type"
            value={formHook.form.type}
            getValue={getValue}
            editing={isEditing}
            options={accessoryOptions}
            {...formHook}
          />
        );
      },
    },
    {
      id: "quantity",
      header: "Quantity",
      accessor: "quantity",
      accessorFn: (originalRow) => originalRow.quantity,
      minSize: 110,
      cell: ({ getValue, row }) => {
        const isEditing = row.original.editing;
        return (
          <TableItem
            id="quantity"
            type="number"
            placeholder="Quantity"
            value={formHook.form.quantity}
            getValue={getValue}
            editing={isEditing}
            displayText={(v) => (
              <span className="font-semibold text-xl">{getValue()}</span>
            )}
            {...formHook}
          />
        );
      },
    },
    {
      id: "unitPrice",
      header: "Unit Price",
      accessor: "unitPrice",
      remove: !canViewMoney,
      accessorFn: (originalRow) => originalRow.unitPrice,
      minSize: 150,
      cell: ({ getValue, row }) => {
        const isEditing = row.original.editing;
        const amount = renderAmount(getValue());
        return (
          <TableItem
            id="unitPrice"
            type="number"
            placeholder="Unit Price"
            value={formHook.form.unitPrice}
            getValue={getValue}
            editing={isEditing}
            icon={{
              left: <div className="text-gray-500 text-sm">$</div>,
            }}
            {...formHook}
          />
        );
      },
    },
    {
      id: "total",
      header: "Total",
      accessor: "total",
      remove: !canViewMoney,
      accessorFn: (originalRow) => originalRow.total,
      cell: ({ row, getValue }) => {
        const total =
          Number(formHook.form.unitPrice) * Number(formHook.form.quantity);
        return renderAmount(total || getValue());
      },
      minSize: 150,
    },
    {
      id: "actions",
      // header: "Actions",
      remove: !canViewMoney,
      accessor: "actions",
      cell: ({ row }) => {
        const isEditing = row.original.editing;
        return (
          <div className="flex space-x-2">
            {isEditing ? (
              "Save"
            ) : (
              <>
                <button
                  type="button"
                  className="p-1 rounded-md hover:bg-gray-100 text-blue-600 hover:text-blue-700"
                  onClick={() => editAcc(row.original)}
                  title="Edit accessory"
                >
                  <PencilSquareIcon className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  className="p-1 rounded-md hover:bg-gray-100 text-red-600 hover:text-red-700"
                  onClick={() => removeAcc(row.original)}
                  title="Remove accessory"
                >
                  <XCircleIcon className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        );
      },
      minSize: 150,
    },
  ];

  const columns = useMemo(() => renderCustomColumnDefs(tableCols), []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const headers = table.getHeaderGroups();
  const grandTotal = canViewMoney ? renderAmount(_.sumBy(data, "total")) : null;

  if (!data || data.length === 0) {
    return (
      <div className="py-10">
        <div className="text-center">
          <FolderPlusIcon className="w-12 h-12 mx-auto text-gray-400 stroke-1" />
          <h3 className="mt-2 font-semibold text-sm">No Accessories</h3>
          <p className="text-sm text-gray-500">
            Get started by adding an accessory to this {jobLower}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div
        className="min-w-full"
        style={
          {
            width: table.getCenterTotalSize(),
          }
        }
      >
        <div className="bg-gray-50 border-y border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide">
          {headers.map((headerGroup, idx) => (
            <div key={idx} className="flex">
              {headerGroup.headers.map((header, i) => (
                <div
                  {...{
                    key: i,
                    colSpan: header.colSpan,
                    style: {
                      width: header.getSize(),
                    },
                    className: "px-3 py-2",
                  }}
                >
                  <span
                    {...{
                      className: "flex flex-row items-center",
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div>
          {table.getRowModel().rows.map((row, i) => (
            <div
              key={i}
              className="flex border-b border-gray-200 hover:bg-gray-50"
            >
              {row.getVisibleCells().map((cell, idx) => (
                <div
                  {...{
                    key: idx,
                    style: {
                      width: cell.column.getSize(),
                    },
                    className: "px-3 py-3 text-sm text-gray-900",
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      {!canViewMoney ? null : props.footer ? (
        props.footer({ total: grandTotal })
      ) : (
        <div
          className="flex justify-end pt-3"
          style={{
            width: table.getCenterTotalSize(),
          }}
        >
          <div>
            <div className="flex space-x-9 items-end">
              <div className="text-sm font-semibold text-gray-700">Total</div>
              <div className="text-sm text-gray-900">{grandTotal}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessoryTable;

const TableItem = ({
  type,
  placeholder,
  editing,
  value,
  options,
  id,
  icon,
  updateForm,
  getValue,
}) => {
  function handleChange(e) {
    updateForm({ id: id, value: e.value });
  }

  return (
    <EditableText
      id={id}
      disabled
      className="mr-2"
      onChange={handleChange}
      type={type}
      placeholder={placeholder}
      editing={editing}
      value={value || getValue()}
      hideActions
      options={options}
      icon={icon}
    />
  );
};
